// ============================================================================
// WaiverReportScreen — Relaxations & Waivers (PMS Phase 2 slice 7c-2)
// ============================================================================
// Every stay charge the stay policy entitled the property to — early check-in,
// late check-out, retention of unused nights — that an authorised user charged
// less for: reduced, waived in full, or reversed afterwards. Who allowed it, why,
// and what was given up, so no relaxation of the property's billing goes unseen
// (BR-STAY-006). Dated by the voucher date (a reversal by its own date).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell } from 'components/enterprise-shell';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import { reportService } from 'api/services/reportService';
import { propertyService } from 'api/services/propertyService';
import notificationService from 'services/notificationService';
import { blobErrorMessage, reserveTab, saveReportFile, showPdf, type ReportFormat } from 'utils/reportFile';
import { formatCurrency } from 'utils/currencyFormat';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import type { ReportFilter, WaiverReport, WaiverRow } from 'api/types';
import 'components/modules/dashboard-module/dashboard.css';

const isoOf = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const todayIso = () => isoOf(new Date());
const monthStartIso = () => { const d = new Date(); d.setDate(1); return isoOf(d); };
const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const KIND: Record<string, string> = { RETENTION: 'Retention', LATE_CHECKOUT: 'Late check-out', EARLY_CHECKIN: 'Early check-in' };
const ACTION: Record<string, { label: string; badge: string }> = {
  REDUCED: { label: 'Reduced', badge: 'badge-info' },
  WAIVED: { label: 'Waived', badge: 'badge-warning' },
  REVERSED: { label: 'Reversed', badge: 'badge-danger' },
};

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** RBAC — {'view','export'}. Fail-closed: [] renders no export/print. */
  allowedActions?: string[];
}

type Row = WaiverRow & { id: string };

const WaiverReportScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const [propertyId, setPropertyId] = useState('');
  const { defaultPropertyId } = useDefaultProperty();
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  const [from, setFrom] = useState(monthStartIso());
  const [to, setTo] = useState(todayIso());
  const [filterOpen, setFilterOpen] = useState(false);
  const propertyLabelsRef = useRef<Record<string, string>>({});
  const fetchedPropertyLabel = usePropertyLabel(propertyId);
  const propertyLabel = fetchedPropertyLabel || propertyLabelsRef.current[propertyId] || '';

  const enabled = !!propertyId && !!from && !!to && from <= to;
  const filter: ReportFilter = useMemo(() => ({ propertyId, from, to }), [propertyId, from, to]);

  const [report, setReport] = useState<WaiverReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!enabled) { setReport(null); return; }
    setLoading(true);
    setError(null);
    try {
      setReport(await reportService.getWaivers(filter));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'The report did not load.');
    } finally {
      setLoading(false);
    }
  }, [enabled, filter]);
  useEffect(() => { void load(); }, [load]);

  const loadProperties = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 500, includeInactive: false });
    const opts = (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
    opts.forEach((o: SelectOption) => { propertyLabelsRef.current[o.value as string] = o.label; });
    return opts;
  }, []);

  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const exportFile = useCallback(async (format: ReportFormat) => {
    if (!enabled) { notificationService.toast.warning('Select a property and a valid period first.'); return; }
    const tab = format === 'pdf' ? reserveTab() : null;
    (format === 'pdf' ? setPrinting : setExporting)(true);
    try {
      const { blob, disposition } = await reportService.exportWaivers(filter, format);
      if (format === 'pdf') showPdf(blob, tab);
      else saveReportFile(blob, disposition, `Waivers_${from}_${to}.xlsx`);
    } catch (err) {
      tab?.close();
      notificationService.toast.error(await blobErrorMessage(err, format === 'pdf' ? 'Print failed.' : 'Export failed.'));
    } finally {
      (format === 'pdf' ? setPrinting : setExporting)(false);
    }
  }, [enabled, filter, from, to]);

  const rows = useMemo<Row[]>(() => (report?.rows ?? []).map((r, i) => ({ ...r, id: `${r.voucherId}-${r.action}-${i}` })), [report]);

  const columns = useMemo<SyutGridColumn<Row>[]>(() => [
    { id: 'date', header: 'Date', accessorKey: 'date', size: 110, cell: ({ row }) => formatDate(row.original.date) },
    { id: 'voucherNumber', header: 'Voucher', accessorKey: 'voucherNumber', size: 150, cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.voucherNumber}</span> },
    {
      id: 'guestName', header: 'Guest / Booking', accessorKey: 'guestName', size: 190,
      cell: ({ row }) => (
        <span>{row.original.guestName || '—'}
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{row.original.bookingNumber}{row.original.roomNumber ? ` · room ${row.original.roomNumber}` : ''}</div>
        </span>
      ),
    },
    {
      id: 'kind', header: 'Charge', accessorKey: 'kind', size: 140,
      cell: ({ row }) => (
        <span>{KIND[row.original.kind] ?? row.original.kind}
          {row.original.serviceDate && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>for {formatDate(row.original.serviceDate)}</div>}
        </span>
      ),
    },
    {
      id: 'action', header: 'Action', accessorKey: 'action', size: 100,
      cell: ({ row }) => {
        const a = ACTION[row.original.action] ?? { label: row.original.action, badge: 'badge-secondary' };
        return <span className={`badge ${a.badge}`} style={{ fontSize: '0.62rem' }}>{a.label}</span>;
      },
    },
    { id: 'entitled', header: 'Entitled', accessorKey: 'entitled', size: 110, cell: ({ row }) => formatCurrency(row.original.entitled) },
    { id: 'charged', header: 'Charged', accessorKey: 'charged', size: 110, cell: ({ row }) => formatCurrency(row.original.charged) },
    { id: 'givenUp', header: 'Given up', accessorKey: 'givenUp', size: 110, cell: ({ row }) => <b style={{ color: 'var(--danger, #dc2626)' }}>{formatCurrency(row.original.givenUp)}</b> },
    { id: 'reason', header: 'Reason', accessorKey: 'reason', size: 260, cell: ({ row }) => <span style={{ whiteSpace: 'normal', fontSize: '0.8rem' }}>{row.original.reason || '—'}</span> },
    { id: 'allowedBy', header: 'Allowed by', accessorKey: 'allowedBy', size: 140, cell: ({ row }) => row.original.allowedBy || '—' },
  ], []);

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => { void load(); }, variant: 'icon' },
    ];
    if (allowedActions.includes('export')) {
      buttons.push({ id: 'export', icon: <IconSystem.download size={16} />, label: exporting ? 'Exporting…' : 'Export', tooltip: 'Export to Excel', onClick: () => { void exportFile('xlsx'); }, variant: 'icon' });
      buttons.push({ id: 'print', icon: <IconSystem.print size={16} />, label: printing ? 'Preparing…' : 'Print', tooltip: 'Print the register', onClick: () => { void exportFile('pdf'); }, variant: 'icon' });
    }
    return buttons;
  }, [load, allowedActions, exporting, printing, exportFile]);

  const periodLabel = from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;
  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : 'Select in filters'), primary: true },
    { label: 'Period', value: periodLabel, primary: true, title: 'Voucher dates (a reversal by its own date)' },
    ...(report ? [
      { label: 'Relaxations', value: String(report.rows.length) },
      { label: 'Entitled', value: formatCurrency(report.entitled) },
      { label: 'Charged', value: formatCurrency(report.charged) },
      { label: 'Given up', value: formatCurrency(report.givenUp), title: 'What the stay policy entitled the property to and was not charged' },
    ] : []),
  ];

  const footer = report && report.rows.length > 0 ? (
    <>
      <b>By charge</b>
      {report.byKind.map((k) => <span key={k.key}>{KIND[k.key] ?? k.key} {k.count} · <b>{formatCurrency(k.givenUp)}</b></span>)}
      <b style={{ marginLeft: '0.75rem' }}>By user</b>
      {report.byUser.map((u) => <span key={u.key}>{u.key} {u.count} · <b>{formatCurrency(u.givenUp)}</b></span>)}
    </>
  ) : undefined;

  return (
    <EnterpriseScreenShell
      title="Relaxations & Waivers"
      description="Stay charges the property was entitled to and charged less for — reduced, waived or reversed — with who allowed it and why."
      icon={<IconSystem.list size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount: (from !== monthStartIso() || to !== todayIso()) ? 1 : 0 }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label={<>Property <span className="text-danger">*</span></>}
                  value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')}
                  isAsync
                  loadOptions={loadProperties}
                  defaultOptions={true}
                  cacheOptions={false}
                  isClearable={false}
                  menuPortalTarget={document.body}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>At most a year.</div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setFrom(monthStartIso()); setTo(todayIso()); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody footer={footer}>
        {!enabled ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Pick a property and a period in the filters.</div>
        ) : error ? (
          <div style={{ color: 'var(--danger, #ef4444)', padding: '2rem', textAlign: 'center' }}>Failed to load — {error}</div>
        ) : (
          <SyutGrid data={rows} columns={columns} isLoading={loading} emptyMessage="Nothing was relaxed or waived in the period." />
        )}
      </ReportBody>
    </EnterpriseScreenShell>
  );
};

export default WaiverReportScreen;
